import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  // Explicitly declare props to satisfy strict TypeScript environments
  declare props: Readonly<Props>;

  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4 font-sans">
          <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md text-center border border-slate-200">
            <div className="bg-red-50 p-4 rounded-full inline-block mb-4">
              <AlertTriangle size={48} className="text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800 mb-2">Something went wrong</h1>
            <p className="text-slate-500 mb-6 text-sm leading-relaxed">
              We encountered an unexpected error while loading this section.
              <br />
              <span className="text-xs font-mono bg-slate-100 px-1 py-0.5 rounded mt-2 inline-block max-w-full truncate">
                {this.state.error?.message || "Unknown Error"}
              </span>
            </p>

            <div className="flex flex-col gap-3">
              <button
                onClick={() => window.location.reload()}
                className="bg-indigo-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-indigo-700 transition flex items-center justify-center gap-2 shadow-lg shadow-indigo-200"
              >
                <RefreshCw size={20} /> Reload Application
              </button>

              <div className="flex gap-2 justify-center mt-2">
                <button
                  onClick={() => {
                    localStorage.clear();
                    window.location.reload();
                  }}
                  className="text-slate-400 text-xs font-medium hover:text-red-500 transition px-3 py-2"
                >
                  Clear Data & Reset
                </button>
                <a
                  href="/"
                  className="text-slate-400 text-xs font-medium hover:text-indigo-500 transition px-3 py-2 flex items-center gap-1"
                >
                  <Home size={12} /> Go Home
                </a>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
